<template>
  <div v-if="visible" class="rdd-overlay" @click.self="close" @keydown.esc="close">
    <div class="rdd-modal">

      <!-- Header -->
      <div class="rdd-head">
        <div class="rdd-head-row">
          <div class="rdd-title">{{ $t("insights.risk_drilldown.title") }}</div>
          <button class="rdd-close" @click="close">&times;</button>
        </div>
        <div class="rdd-week-tabs" ref="weekTabs">
          <div
            v-for="(date, idx) in weekDates"
            :key="idx"
            class="rdd-wtab"
            :class="{ active: idx === selectedWeekIndex }"
            @click="selectWeek(idx)"
          >
            {{ date }}
            <span class="rdd-ct">{{ weekRiskCounts[idx] }}</span>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="rdd-body">

        <!-- Legend -->
        <div class="rdd-legend">
          <div class="rdd-legend-item">
            <div class="rdd-legend-bar" style="background: #900505"></div>{{ $t("insights.risk_drilldown.high") }}
          </div>
          <div class="rdd-legend-item">
            <div class="rdd-legend-bar" style="background: #C41919"></div>{{ $t("insights.risk_drilldown.med") }}
          </div>
          <div class="rdd-legend-item">
            <div class="rdd-legend-bar" style="background: #E25555"></div>{{ $t("insights.risk_drilldown.low") }}
          </div>
          <div class="rdd-legend-item">
            <div class="rdd-legend-bar" style="background: #35d077"></div>{{ $t("insights.risk_drilldown.recovered") }}
          </div>
          <div class="rdd-legend-item">
            <div class="rdd-legend-bar" style="background: rgba(0,0,0,0.06)"></div>{{ $t("insights.risk_drilldown.no_change") }}
          </div>
          <div class="rdd-legend-item">
            <div class="rdd-legend-bar rdd-legend-selected"></div>{{ $t("insights.risk_drilldown.selected") }}
          </div>
        </div>

        <!-- Column header -->
        <div class="rdd-col-header">
          <div class="rdd-col-name">{{ $t("insights.risk_drilldown.col_name") }}</div>
          <div class="rdd-col-freq">{{ $t("insights.risk_drilldown.col_frequency") }}</div>
          <div class="rdd-col-hist">{{ $t("insights.risk_drilldown.col_history") }}</div>
        </div>

        <template v-for="section in sections">
          <template v-if="section.people.length">
            <div v-if="section.sep" :key="section.key + '-sep'" class="rdd-sep"></div>
            <div :key="section.key + '-label'" class="rdd-slabel">{{ section.label }}</div>
            <div
              v-for="person in section.people"
              :key="section.key + '-' + person.email"
              class="rdd-pcard"
              :class="{ 'rdd-archived': person.isArchived }"
            >
              <div class="rdd-avatar" :style="{ background: person.profilepic ? 'transparent' : avatarColor(person) }">
                <img v-if="person.profilepic" v-lazy="person.profilepic" :key="person.profilepic" class="rdd-avatar-img image-skeleton" />
                <span v-else>{{ $utils("acronymName", { firstName: person.firstname, lastName: person.lastname }) }}</span>
              </div>
              <div class="rdd-pinfo">
                <div class="rdd-pname">{{ person.firstname }} {{ person.lastname }}<span v-if="person.isArchived" class="rdd-exited-tag">{{ $t("insights.risk_drilldown.exited") }}</span></div>
                <div class="rdd-pteam">{{ person.team_name }}</div>
                <div class="rdd-prisk" :title="person.risk_due">{{ person.risk_due }}</div>
              </div>
              <div class="rdd-freq" :class="{ 'high-freq': person.riskFrequency >= 3 }">
                {{ person.riskFrequency }} <small>{{ $t("insights.risk_drilldown.weeks_short") }}</small>
              </div>
              <div class="rdd-tstrip">
                <div
                  v-for="(level, wIdx) in person.weeklyRisk"
                  :key="wIdx"
                  class="rdd-tdot"
                  :class="[dotClass(level), { now: wIdx === selectedWeekIndex }]"
                >
                  <span class="rdd-ttip">{{ weekDates[wIdx] }} — {{ levelLabel(level) }}</span>
                </div>
              </div>
            </div>
          </template>
        </template>

      </div>

      <!-- Footer -->
      <div class="rdd-foot">
        <div>{{ atRiskCount }} {{ $t("insights.risk_drilldown.at_risk") }} &middot; {{ recoveredCount }} {{ $t("insights.risk_drilldown.recovered_lc") }}</div>
        <div>{{ $t("insights.risk_drilldown.week_of") }} <strong>{{ weekDates[selectedWeekIndex] }}</strong></div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'RiskDrillDown',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    weekDates: {
      type: Array,
      default: function () { return [] }
    },
    selectedWeekIndex: {
      type: Number,
      default: 0
    },
    riskHistoryData: {
      type: Array,
      default: function () { return [] }
    },
    weekRiskCounts: {
      type: Array,
      default: function () { return [] }
    }
  },
  computed: {
    sections: function () {
      var data = this.riskHistoryData
      var recurring = []
      var firstTime = []
      var recovered = []
      for (var i = 0; i < data.length; i++) {
        if (data[i].category === 'recurring') recurring.push(data[i])
        else if (data[i].category === 'first_time') firstTime.push(data[i])
        else if (data[i].category === 'recovered') recovered.push(data[i])
      }
      return [
        { key: 'rec', label: this.$t('insights.risk_drilldown.recurring'), sep: false, people: recurring },
        { key: 'ft', label: this.$t('insights.risk_drilldown.first_time'), sep: true, people: firstTime },
        { key: 'rv', label: this.$t('insights.risk_drilldown.recovered'), sep: true, people: recovered }
      ]
    },
    atRiskCount: function () {
      return this.weekRiskCounts[this.selectedWeekIndex] || 0
    },
    recoveredCount: function () {
      var s = this.sections
      for (var i = 0; i < s.length; i++) {
        if (s[i].key === 'rv') return s[i].people.length
      }
      return 0
    }
  },
  methods: {
    close: function () {
      this.$emit('update:visible', false)
    },
    selectWeek: function (idx) {
      this.$emit('update:selectedWeekIndex', idx)
    },
    avatarColor: function (person) {
      var colors = [
        '#e67e22', '#6366f1', '#ec4899', '#0ea5e9',
        '#3b82f6', '#14b8a6', '#10b981', '#8b5cf6',
        '#f43f5e', '#f59e0b', '#06b6d4', '#84cc16'
      ]
      var str = (person.email || person.firstname || '')
      var hash = 0
      for (var i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
      }
      return colors[Math.abs(hash) % colors.length]
    },
    dotClass: function (level) {
      if (level === '3') return 'h'
      if (level === '2') return 'm'
      if (level === '1') return 'l'
      if (level === '0') return 'r'
      return ''
    },
    levelLabel: function (level) {
      if (level === '3') return this.$t('insights.risk_drilldown.high')
      if (level === '2') return this.$t('insights.risk_drilldown.med')
      if (level === '1') return this.$t('insights.risk_drilldown.low')
      if (level === '0') return this.$t('insights.risk_drilldown.recovered')
      return this.$t('insights.risk_drilldown.no_change')
    },
    scrollToActiveTab: function () {
      var container = this.$refs.weekTabs
      if (!container) return
      var activeTab = container.querySelector('.rdd-wtab.active')
      if (!activeTab) return
      activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    },
    handleKeydown: function (e) {
      if (e.key === 'Escape' && this.visible) {
        this.close()
      }
    }
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.$nextTick(this.scrollToActiveTab)
      }
    },
    selectedWeekIndex: function () {
      this.$nextTick(this.scrollToActiveTab)
    }
  },
  mounted: function () {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy: function () {
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>

<style scoped lang="scss">
/* CSS variables */
$purple: #6C30CC;
$purple-light: #6c30cc12;
$risk-high: #900505;
$risk-med: #C41919;
$risk-low: #E25555;
$recovery: #35d077;
$fc-primary: #000000;
$fc-dark: #3c3c3c;
$fc-secondary: #626262;
$fc-muted: #9d9d9d;
$card-shadow: 0px 0px 6px #0000001F;
$border-light: rgba(0,0,0,0.06);

/* Overlay */
.rdd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
  z-index: 2000;
}

/* Modal */
.rdd-modal {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.14);
  width: 700px;
  max-width: 94vw;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
}

/* Header */
.rdd-head {
  padding: 20px 24px 0;
  flex-shrink: 0;
}

.rdd-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rdd-title {
  font-size: 18px;
  font-weight: 700;
  color: $fc-primary;
}

.rdd-close {
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  font-size: 18px;
  color: $fc-muted;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f5f5f5;
    color: $fc-dark;
  }
}

/* Week tabs */
.rdd-week-tabs {
  display: flex;
  gap: 0;
  margin-top: 14px;
  border-bottom: 1px solid $border-light;
  overflow-x: auto;
}

.rdd-wtab {
  padding: 8px 14px 10px;
  font-size: 11px;
  font-weight: 600;
  color: $fc-muted;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: color 0.15s;

  &:hover {
    color: $fc-secondary;
  }

  &.active {
    color: $purple;
    border-bottom-color: $purple;

    .rdd-ct {
      background: $purple-light;
      color: $purple;
    }
  }
}

.rdd-ct {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  margin-left: 4px;
  padding: 1px 5px;
  border-radius: 8px;
  background: #f4433615;
  color: $risk-med;
}

/* Body */
.rdd-body {
  padding: 14px 24px 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

/* Legend */
.rdd-legend {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 0 0 10px;
}

.rdd-legend-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: $fc-muted;
}

.rdd-legend-bar {
  width: 6px;
  height: 10px;
  border-radius: 6px;
}

.rdd-legend-selected {
  outline: 2px solid $purple;
  outline-offset: -1px;
  background: #fff;
}

/* Column header */
.rdd-col-header {
  display: flex;
  align-items: center;
  padding: 0 10px 6px 72px;
  font-size: 10px;
  font-weight: 700;
  color: $fc-muted;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.rdd-col-name {
  flex: 1;
}

.rdd-col-freq {
  width: 72px;
  text-align: center;
}

.rdd-col-hist {
  width: 76px;
  text-align: center;
}

/* Section separator / label */
.rdd-slabel {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $fc-muted;
  margin: 18px 0 4px;
}

.rdd-sep {
  height: 1px;
  background: $border-light;
  margin: 14px 0 0;
}

/* Person card row */
.rdd-pcard {
  background: #fff;
  box-shadow: $card-shadow;
  border-radius: 6px;
  min-height: 52px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  padding: 8px 12px 8px 10px;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.01);
    cursor: pointer;
  }

  &.rdd-archived {
    opacity: 0.7;
  }
}

.rdd-exited-tag {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 1px 6px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.08);
  color: $fc-muted;
  margin-left: 6px;
  vertical-align: middle;
}

/* Avatar */
.rdd-avatar {
  width: 36px;
  height: 36px;
  border-radius: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  margin-right: 10px;
  overflow: hidden;
}
.rdd-avatar-img {
  width: 36px;
  height: 36px;
  border-radius: 36px;
  object-fit: cover;
}

/* Name block */
.rdd-pinfo {
  flex: 1;
  min-width: 0;
}

.rdd-pname {
  font-size: 13px;
  font-weight: 600;
  color: $fc-primary;
}

.rdd-pteam {
  font-size: 11px;
  font-weight: 500;
  color: $fc-dark;
  opacity: 0.7;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rdd-prisk {
  font-size: 10px;
  color: $fc-muted;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Frequency count */
.rdd-freq {
  width: 72px;
  text-align: center;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: $fc-dark;

  &.high-freq {
    color: $risk-high;
  }

  small {
    font-size: 10px;
    font-weight: 500;
    color: $fc-muted;
  }
}

/* Timeline strip */
.rdd-tstrip {
  display: flex;
  gap: 2px;
  align-items: center;
  flex-shrink: 0;
  width: 76px;
  justify-content: center;
}

.rdd-tdot {
  width: 6px;
  height: 18px;
  border-radius: 6px;
  background: rgba(0,0,0,0.06);
  position: relative;
  cursor: default;

  &.h { background: $risk-high; }
  &.m { background: $risk-med; }
  &.l { background: $risk-low; }
  &.r { background: $recovery; }
  &.now { outline: 2px solid $purple; outline-offset: 1px; }
}

/* Tooltip */
.rdd-ttip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #2b2b2b;
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s;
  z-index: 5;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #2b2b2b;
  }
}

.rdd-tdot:hover .rdd-ttip {
  opacity: 1;
}

/* Footer */
.rdd-foot {
  padding: 10px 24px;
  border-top: 1px solid $border-light;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  font-size: 11px;
  color: $fc-secondary;
}
</style>
